import {AxiosInstance, AxiosRequestConfig} from "axios";
import JobData from "../model/job/JobData";
import Job from "../model/job/Job";
import {JobEventApiDto, UpdateJobAgentApiDto, UpdateJobStatusApiDto} from "./model/rest";
import JobEvent, {JobEventTypeEnum} from "../model/job/event/JobEvent";
import JobStatusEvent from "../model/job/event/JobStatusEvent";
import JobAppointmentEvent from "../model/job/event/JobAppointmentEvent";
import JobAppointmentStatusEvent from "../model/job/event/JobAppointmentStatusEvent";
import JobFilesAttachedEvent from "../model/job/event/JobFilesAttachedEvent";
import JobAgentChangedEvent from "../model/job/event/JobAgentChangedEvent";
import JobBDPChangedEvent from "../model/job/event/JobBDPChangedEvent";
import JobWatcherEvent from "../model/job/event/JobWatcherEvent";
import JobSmsSentEvent from "../model/job/event/JobSmsSentEvent";
import JobQuoteAcceptedEvent from "../model/job/event/JobQuoteAcceptedEvent";
import JobSurveyCreatedEvent from "../model/job/event/JobSurveyCreatedEvent";
import JobExternalCallRegisteredEvent from "../model/job/event/JobExternalCallRegisteredEvent";
import JobEmailSentEvent from "../model/job/event/JobEmailSentEvent";
import JobSurveyFilesAttachedEvent from "../model/job/event/JobSurveyFilesAttachedEvent";
import JobDetailsModel from "../model/job/JobDetailsModel";
import CustomFieldValue from "../model/job/CustomFieldValue";
import JobInvoicePathChangedEvent from "../model/job/event/JobInvoicePathChangedEvent";
import JobFilesDeletedEvent from "../model/job/event/JobFilesDeletedEvent";
import {isDefined} from "../utils/Utils";
import JobMarkedAsPaidEvent from "../model/job/event/JobMarkedAsPaidEvent";
import JobUnmarkedAsPaidEvent from "../model/job/event/JobUnMarkedAsPaidEvent";
import { EcmsImportResult } from '../components/job/ecmsImport/EcmsImportTypes';
import download = require("downloadjs");

export default class JobApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Creates job
	 * @param {JobData} data Job data transfer object.
	 * @returns {Promise<Job>} Promise containing created Job.
	 */
	createJob(data: JobData): Promise<Job> {
		const url = `/api/web/jobs`;
		return this.axiosInstance.post(url, data.toJson()).then(resp => Job.fromJson(resp.data));
	}

	/**
	 * Fetch single job
	 * @param {string} jobId Id of job to fetch.
	 * @returns {Promise<Job>} Promise containing fetched job.
	 */
	fetchJob(jobId: string): Promise<Job> {
		const url = `/api/web/jobs/${jobId}`;
		return this.axiosInstance.get(url).then(resp => Job.fromJson(resp.data));
	}

	/**
	 * Fetch single job by ref number
	 * @returns {Promise<Job>} Promise containing fetched job.
	 * @param referenceNumber
	 */
	fetchJobByRefNumber(referenceNumber: string): Promise<Job> {
		const url = `/api/web/jobs/reference_number/${referenceNumber}`;
		return this.axiosInstance.get(url).then(resp => Job.fromJson(resp.data));
	}

	/**
	 * Update job
	 * @param {Job} job
	 * @returns {Promise<any>}
	 */
	updateJob(job: Job) {
		const url = `/api/web/jobs/${job.id}`;
		return this.axiosInstance.put(url, job.data.toJson()).then(resp => Job.fromJson(resp.data));
	}

	/**
	 * Update job status
	 * @param {string} jobId
	 * @param {UpdateJobStatusApiDto} data
	 * @returns {Promise<void>}
	 */
	updateStatus(jobId: string, data: UpdateJobStatusApiDto): Promise<void> {
		const url = `/api/web/jobs/${jobId}/status`;
		return this.axiosInstance.put(url, data).then(() => {})
	}

	/**
	 * Attach attachments to job
	 * @param {string} jobId
	 * @param {string[]} attachmentIds
	 * @returns {Promise<any>}
	 */
	attachUploadedFiles(jobId: string, attachmentIds: string[]): Promise<any> {
		const url = `/api/web/jobs/${jobId}/attachments`;
		return this.axiosInstance.post(url, {ids: attachmentIds});
	}

	/**
	 * Remove attachments from job event
	 * @param jobId
	 * @param attachmentIds
	 */
	removeFiles(jobId: string, attachmentIds: string[]) {
		const url = `/api/web/jobs/${jobId}/attachments`;
		return this.axiosInstance.delete(url, {data: attachmentIds});
	}

	/**
	 * Set tags to job
	 * @param {string} jobId
	 * @param {string[]} tags
	 * @returns {AxiosPromise<any>}
	 */
	setJobTags(jobId: string, tags: string[]) {
		const url = `/api/web/jobs/${jobId}/tags`;
		return this.axiosInstance.put(url, {names: tags});
	}

	/**
	 * Update job agent assignee
	 * @param {string} jobId
	 * @param {UpdateJobAgentApiDto} data
	 * @returns {Promise<any>}
	 */
	updateJobAgent(jobId: string, data: UpdateJobAgentApiDto): Promise<any> {
		const url = `/api/web/jobs/${jobId}/agent`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Fetch job events.
	 * @param {string} jobId
	 * @returns {Promise<JobEvent[]>}
	 */
	fetchEvents(jobId: string): Promise<JobEvent[]> {
		const url = `/api/web/jobs/${jobId}/events`;
		return this.axiosInstance.get(url).then(resp => resp.data.map((event: JobEventApiDto) => {
			switch (event.type) {
				case JobEventTypeEnum.Created:
					return JobEvent.fromJson(event);
				case JobEventTypeEnum.Updated:
					return JobEvent.fromJson(event);
				case JobEventTypeEnum.CreatedFromQuote:
					return JobEvent.fromJson(event);
				case JobEventTypeEnum.StatusChanged:
					return JobStatusEvent.fromJson(event);
				case JobEventTypeEnum.AppointmentCreated:
					return JobAppointmentEvent.fromJson(event);
				case JobEventTypeEnum.AppointmentUpdated:
					return JobAppointmentEvent.fromJson(event);
				case JobEventTypeEnum.AppointmentStatusChanged:
					return JobAppointmentStatusEvent.fromJson(event);
				case JobEventTypeEnum.JobFilesAttached:
					return JobFilesAttachedEvent.fromJson(event);
				case JobEventTypeEnum.JobFilesDeleted:
					return JobFilesDeletedEvent.fromJson(event);
				case  JobEventTypeEnum.AgentAssigneeChanged:
					return JobAgentChangedEvent.fromJson(event);
				case JobEventTypeEnum.BdpAssigneeChanged:
					return JobBDPChangedEvent.fromJson(event);
				case JobEventTypeEnum.WatchersChanged:
					return JobWatcherEvent.fromJson(event);
				case JobEventTypeEnum.JobSmsSent:
					return JobSmsSentEvent.fromJson(event);
				case JobEventTypeEnum.QuoteAccepted:
					return JobQuoteAcceptedEvent.fromJson(event);
				case JobEventTypeEnum.SurveyCreated:
					return JobSurveyCreatedEvent.fromJson(event);
				case JobEventTypeEnum.ExternalCallRegistered:
					return JobExternalCallRegisteredEvent.fromJson(event);
				case JobEventTypeEnum.JobEmailSent:
					return JobEmailSentEvent.fromJson(event);
				case JobEventTypeEnum.SurveyFilesAttached:
					return JobSurveyFilesAttachedEvent.fromJson(event);
				case JobEventTypeEnum.InvoicePathChanged:
					return JobInvoicePathChangedEvent.fromJson(event);
				case JobEventTypeEnum.MarkedAsPaid:
					return JobMarkedAsPaidEvent.fromJson(event);
				case JobEventTypeEnum.UnMarkedAsPaid:
					return JobUnmarkedAsPaidEvent.fromJson(event);
				default:
					// fallback
					return null;
			}
		}).filter((e: JobEvent) => e != null));
	}

	/**
	 * Update job watchers
	 * @param {string} jobId
	 * @param {string[]} watchers
	 * @returns {Promise<void>}
	 */
	updateJobWatchers(jobId: string, watchers: string[]): Promise<void> {
		const url = `/api/web/jobs/${jobId}/watchers`;
		return this.axiosInstance.put(url, watchers).then(() => {})
	}

	/**
	 * Mention users
	 * @param {string} jobId
	 * @param userIds
	 * @param noteId
	 * @returns {AxiosPromise<any>}
	 */
	mentionUsers(jobId: string, userIds: string[], noteId?: string) {
		const url = `/api/web/jobs/${jobId}/mentions`;
		return this.axiosInstance.post(url, {userIds, noteId})
	}

	/**
	 * Send e-mail to job contact.
	 * @param {string} jobId
	 * @param {string} subject
	 * @param {string} message
	 * @returns {Promise<any>}
	 */
	sendEmailToJobContact(jobId: string, subject: string, message: string): Promise<any> {
		const url = `/api/web/jobs/${jobId}/contact_send_email`;
		return this.axiosInstance.put(url, {subject, message})
	}

	/**
	 * Add phone call event
	 * @param {string} jobId
	 * @param {string} conversationContent
	 * @returns {Promise<any>}
	 */
	addCallEvent(jobId: string, conversationContent: string): Promise<any> {
		const url = `/api/web/jobs/${jobId}/external_calls`;
		return this.axiosInstance.post(url, {conversationContent})
	}

	/**
	 * Send SMS to job contact.
	 * @param {string} jobId
	 * @param {string} message
	 * @returns {Promise<any>}
	 */
	sendSmsToJobContact(jobId: string, message: string): Promise<any> {
		const url = `/api/web/jobs/${jobId}/contact_send_sms`;
		return this.axiosInstance.put(url, {message})
	}

	/**
	 * Fetch Job details
	 * @param {string} jobId
	 * @returns {Promise<JobDetailsModel>}
	 */
	fetchJobDetails(jobId: string): Promise<JobDetailsModel> {
		const url = `/api/web/jobs/${jobId}/details`;
		return this.axiosInstance.get(url).then(resp => JobDetailsModel.fromJson(resp.data))
	}

	/**
	 * Update job details
	 * @param {string} jobId
	 * @param {JobDetailsModel} details
	 * @returns {Promise<JobDetailsModel>}
	 */
	updateJobDetails(jobId: string, details: JobDetailsModel): Promise<JobDetailsModel> {
		const url = `/api/web/jobs/${jobId}/details`;
		return this.axiosInstance.put(url, details.toJson()).then(resp => JobDetailsModel.fromJson(resp.data))
	}

	/**
	 * Generate and download csv warranty registration file to GDrive
	 * @param {string} jobId
	 * @returns {Promise<any>}
	 */
	downloadWarrantyCsv(jobId: string): Promise<any> {
		const url = `/api/web/jobs/${jobId}/warranty_registration`;
		return this.axiosInstance.post(url);
	}

	/**
	 * Fetch Job custom fields values
	 * @param {string} jobId
	 * @returns {Promise<CustomFieldValue>}
	 */
	fetchJobCustomFieldsValues(jobId: string): Promise<CustomFieldValue[]> {
		const url = `/api/web/jobs/${jobId}/custom_field_values`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(CustomFieldValue.fromJson));
	}

	/**
	 * Create job custom field value
	 * @param {string} jobId
	 * @param {CustomFieldValue} customFieldValue
	 * @returns {Promise<CustomFieldValue>}
	 */
	createJobCustomFieldValue(jobId: string, customFieldValue: CustomFieldValue): Promise<CustomFieldValue> {
		const url = `/api/web/jobs/${jobId}/custom_field_values`;
		return this.axiosInstance.post(url, customFieldValue.toJson()).then(resp => CustomFieldValue.fromJson(resp.data));
	}

	/**
	 * Update job custom field value
	 * @param {CustomFieldValue} customFieldValue
	 * @returns {Promise<CustomFieldValue>}
	 */
	updateJobCustomFieldValue(customFieldValue: CustomFieldValue): Promise<CustomFieldValue> {
		const url = `/api/web/jobs/custom_field_values/${customFieldValue.id.get}`;
		return this.axiosInstance.put(url, customFieldValue.toJson()).then(resp => CustomFieldValue.fromJson(resp.data));
	}

	/**
	 * Delete job custom field value
	 * @param {string} customFieldValueId
	 * @returns {AxiosPromise}
	 */
	deleteJobCustomFieldValue(customFieldValueId: string) {
		const url = `/api/web/jobs/custom_field_values/${customFieldValueId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Send SMS to job creator.
	 * @param {string} jobId
	 * @param {string} message
	 * @returns {Promise<any>}
	 */
	sendSmsToJobCreator(jobId: string, message: string): Promise<any> {
		const url = `/api/web/jobs/${jobId}/creator_send_sms`;
		return this.axiosInstance.put(url, {message: message});
	}

	/**
	 * Download jobs xlsx
	 * @returns {Promise<void>}
	 */
	downloadJobs() {
		const url = `/api/web/jobs/export`;
		const query: AxiosRequestConfig = {responseType: "blob", timeout: 60000};
		return this.axiosInstance.get(url, query).then((resp) => {
			const disposition = resp.headers['content-disposition'];
			const filename = disposition.match(/filename=(.*)/)[1];
			download(resp.data, isDefined(filename) ? filename : "jobs.xlsx");
		});
	}

	/**
	 * Upload jobs xlsx
	 * @param {File} file
	 */
	uploadJobs(file: File) {
		const url = `/api/web/jobs/import`;
		const config: AxiosRequestConfig = {headers: {'content-type': 'multipart/form-data'}, timeout: 300000};
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}

	importEcmsStatuses(file: File, jobTypeId: string): Promise<EcmsImportResult> {
		const url = `/api/web/jobs/status/import`;
		const config: AxiosRequestConfig = {headers: {'content-type': 'multipart/form-data'}, timeout: 300000};
		const formData = new FormData();
		formData.append('file', file);
		formData.append('jobTypeId', jobTypeId);
		return this.axiosInstance.post(url, formData, config).then(r => r.data);
	}

	/**
	 * Upload job statuses
	 * @param {jobIds} ids
	 * @param {statusId} status id
	 */
	updateJobStatuses(jobIds: string[], statusId: string): Promise<any> {
		const url = `/api/web/jobs/status`;
		return this.axiosInstance.post(url, {jobIds, statusId});
	}

	/**
	 * Unmark as paid
	 * @param jobId
	 */
	unmarkedAsPaid(jobId: string) {
		const url = `/api/web/accounting/invoices/${jobId}/unmark_as_paid`;
		return this.axiosInstance.put(url)
	}
}
